import { debounce } from 'lodash'
import { type NumericFormatProps } from 'react-number-format'

import { type EmptyStateProps } from '../ComponentTypes.models'

/** Props interface for components that support disabling nested hierarchy behavior */
export interface IgnoreNodeHierarchyProps {
  /** Boolean value to indicate if the nested hierarchy is disabled */
  ignoreNodeHierarchy?: boolean
}

export interface GenericNode {
  [key: string]: string | number | null | NumericFormatProps | GenericNode[]
}

export enum CheckboxState {
  UNCHECKED = 'UNCHECKED',
  CHECKED = 'CHECKED',
  PARTIAL = 'PARTIAL',
}

export interface DateCheckState {
  id: string | number
  state: CheckboxState
}

export interface FlattenNestedDataProps {
  [key: string]: string | number | null | NumericFormatProps
}

type NestedMultiselectSearchProps<DataItem> = {
  query: string
  identifierKeys: {
    displayKey: keyof DataItem
    idKey: keyof DataItem
    parentKey: keyof DataItem
  }
  setFilteredItems: React.Dispatch<React.SetStateAction<DataItem[]>>
  nestedConfigData: DataItem[]
  toggleExpand?: (idsToExpand: string[]) => void
}
export interface TableColumnConfigProps<DataItem> {
  /** Specifies the text to be displayed in the table column header, serving as a visual label for the column's data. */
  label: string
  /** Defines the unique key used to retrieve the corresponding data from each row object, linking column configuration to data source. */
  dataKey: string
  /** Optional property that allows custom CSS styling to be applied directly to each cell in the column,  */
  style?: React.CSSProperties
  /** A render prop function that returns a custom React component or JSX for displaying the content of each cell in the column,
   *  providing flexibility for complex data rendering. */
  children: (data: DataItem, index?: number) => React.ReactNode
}

export interface NestedCheckboxProps<DataItem>
  extends IgnoreNodeHierarchyProps {
  /** Data to be displayed in the nested component */
  data: DataItem[]
  /** If there is data available to display. If false, the component will display an empty state */
  hasData: boolean
  /** If the data is still loading. If true, the component will display a loading state */
  isLoading: boolean
  /** Configuration for the table columns */
  tableConfig: TableColumnConfigProps<DataItem>[]
  /** Object containing keys to identify the data and its parent child relationship */
  identifierKeys: {
    displayKey: string
    idKey: string
    parentKey: string
  }
  // TODO: Remove this prop once `search` prop is implemented at all instances of TreeListBox
  /** @deprecated - If the component should have a search bar */
  isSearchable?: boolean
  /** Optional search string to filter data */
  search?: string
  /** Custom height for the table content */
  customHeight?: number | string
  /** Custom width for the table component */
  customWidth?: number | string
  /** Function to be called when the state of the checkboxes change */
  onChange: (newItemStates: DateCheckState[]) => void
  /** Useful when there is no data available such as an empty search result */
  noDataText?: EmptyStateProps
  /** Array of objects to keep the track of selected or unselected options */
  itemStates: DateCheckState[]
  /** Function to set the array of selected options */
  setItemStates: React.Dispatch<React.SetStateAction<DateCheckState[]>>
  /** Optional prop to add a test id for QA testing */
  qaTestId?: string
}
// TODO: Remove any once `items` type is correctly defined.
export interface CheckboxListProps<DataItem = any> {
  // TODO: Define type for items based on more specific data
  items: any[]
  getStateForId: (id: string) => CheckboxState
  idsToRender?: string[]
  onClick?: (id: string) => void
  expandedItems?: { [key: string]: boolean }
  onToggleExpand?: (id: string) => void
  indentLevel?: number
  tableConfig: TableColumnConfigProps<DataItem>[]
  idKey: string
  parentKey: string
  displayKey: string
  pathKey: string
  /** Optional className that can be provided for styling table row */
  className?: string
}

export const updateItemStates = <DataItem>(
  ignoreNodeHierarchy: boolean,
  oldState: DateCheckState[],
  items: DataItem[],
  clickedId: string,
  idKey: keyof DataItem,
  parentKey: keyof DataItem,
) => {
  // Maps for quick access
  const stateMap = new Map<string, CheckboxState>(
    // TODO: Define type for i and state based on more specific data
    oldState.map((i: { [x: string]: any; state: any }) => [
      i[idKey as string],
      i.state,
    ]),
  )

  const itemMap = new Map<string, DataItem>(
    items.map((item) => {
      const key = item[idKey] as string
      return [key, item]
    }),
  )

  const childrenMap = new Map<string, string[]>()
  const parentMap = new Map<string, string>()

  // TODO: Define type for items based on more specific data
  // Initialize children and parent maps for quick navigation
  items.forEach((item: DataItem) => {
    const parentId = item[parentKey] as string
    const itemId = item[idKey] as string

    if (!childrenMap.has(parentId)) {
      childrenMap.set(parentId, [])
    }
    childrenMap.get(parentId)!.push(itemId)
    parentMap.set(itemId, parentId)
  })

  // Recursive function to update the state of an item and its descendants
  const updateDescendants = (id: string, newState: CheckboxState) => {
    stateMap.set(id, newState)
    if (!ignoreNodeHierarchy) {
      const children = childrenMap.get(id) || []
      children.forEach((childId) => updateDescendants(childId, newState))
    }
  }

  // Function to update ancestors based on their children's states
  const updateAncestors = (id: string) => {
    let parentId = parentMap.get(id)
    while (parentId) {
      const childrenIds = childrenMap.get(parentId) || []
      const allChecked = childrenIds.every(
        (id) => stateMap.get(id) === CheckboxState.CHECKED,
      )
      const allUnchecked = childrenIds.every(
        (id) => stateMap.get(id) === CheckboxState.UNCHECKED,
      )

      if (ignoreNodeHierarchy) {
        break
      } else {
        stateMap.set(
          parentId,
          allChecked
            ? CheckboxState.CHECKED
            : allUnchecked
              ? CheckboxState.UNCHECKED
              : CheckboxState.PARTIAL,
        )
      }
      parentId = parentMap.get(parentId)
    }
  }

  // Determine the new state for the clicked item
  const clickedItemNewState =
    stateMap.get(clickedId) === CheckboxState.CHECKED
      ? CheckboxState.UNCHECKED
      : CheckboxState.CHECKED

  // Update the clicked item and its descendants
  updateDescendants(clickedId, clickedItemNewState)

  // Update ancestors of the clicked item
  updateAncestors(clickedId)

  // Convert the state map back to the required array format
  return Array.from(stateMap.keys()).map((id) => ({
    id,
    state: stateMap.get(id)!,
    ...itemMap.get(id),
  }))
}

// flatten the nested data to a flat array of objects until the last level where there are no children
export const getFlattenNestedTreeList = <DataItem>({
  nestedData,
  parentId = null,
  childrenKey = 'children',
  parentKey,
  idKey = 'id',
  pathId = '',
}: {
  nestedData: DataItem[]
  parentId?: string | number | null
  childrenKey?: string
  parentKey: string
  idKey?: string
  pathId?: string
}) => {
  const flatArray: DataItem[] = []
  nestedData.forEach((data) => {
    const { [childrenKey]: children, ...rest } = data as {
      [key: string]: unknown
    }
    const id = data[idKey as keyof typeof data] as string
    const newPathId = pathId ? `${pathId}.${id}` : `${id}`

    flatArray.push({
      ...rest,
      [parentKey]: parentId,
      pathId: newPathId, // Unique path identifier
    } as DataItem)

    if (children && (children as DataItem[]).length > 0) {
      flatArray.push(
        ...getFlattenNestedTreeList({
          nestedData: children as DataItem[],
          parentId: id,
          childrenKey: childrenKey,
          parentKey: parentKey,
          idKey: idKey,
          pathId: newPathId,
        }),
      )
    }
  })
  return flatArray
}

export const debouncedSearch = <DataItem>({
  query,
  identifierKeys,
  setFilteredItems,
  nestedConfigData,
  toggleExpand,
}: NestedMultiselectSearchProps<DataItem>) => {
  const { displayKey, idKey, parentKey } = identifierKeys

  const filterItemsWithNestedSearch = (allItems: DataItem[]) => {
    if (!query) return []

    const queryLower = query.toLowerCase()
    // To store final matched IDs
    const matches = new Set<string>()

    const parentMap = new Map<string, string | null>()
    const childMap = new Map<string, string[]>()

    // Build parent-child relationships
    allItems.forEach((item) => {
      const itemId = item[idKey] as string
      const parentId = item[parentKey] as string

      if (parentId === null || parentId === undefined) {
        parentMap.set(itemId, null) // Root node
      } else {
        parentMap.set(itemId, parentId)
        if (!childMap.has(parentId)) childMap.set(parentId, [])
        // Add item as child of parent
        childMap.get(parentId)!.push(itemId)
      }
    })

    // Find direct matches
    const matchedNodes = new Set<string>()
    allItems.forEach((item) => {
      const displayValue = String(item[displayKey]).toLowerCase()
      const idValue = item[idKey] as string
      if (displayValue.includes(queryLower)) {
        matchedNodes.add(idValue)
      }
    })

    // Collect all ancestors and descendants
    const collectAncestors = (nodeId: string) => {
      let currentId: string | null = nodeId
      while (currentId !== null) {
        if (matches.has(currentId)) break
        matches.add(currentId)
        currentId = parentMap.get(currentId) ?? null
      }
    }

    const collectDescendants = (nodeId: string) => {
      const visited = new Set<string | string>()
      const queue = [nodeId]

      while (queue.length > 0) {
        const currentId = queue.shift()! // Process the first item in the queue

        if (visited.has(currentId)) continue
        // Mark current node as visited
        visited.add(currentId)

        if (!matches.has(currentId)) {
          matches.add(currentId)
        }

        const children = childMap.get(currentId) || []
        children.forEach((childId) => {
          if (!visited.has(childId)) {
            queue.push(childId)
          }
        })
      }
    }

    // Traverse matched nodes to collect ancestors and descendants
    matchedNodes.forEach((nodeId) => {
      collectAncestors(nodeId)
      collectDescendants(nodeId)
    })

    // Convert expanded IDs to string array for toggleExpand
    const expandedIdsArray = Array.from(matches).map(String)
    toggleExpand?.(expandedIdsArray)

    // Filter and return the results
    return allItems.filter((item) => matches.has(item[idKey] as string))
  }

  const debounceSearch = debounce(() => {
    setFilteredItems(filterItemsWithNestedSearch(nestedConfigData ?? []))
  }, 500)

  if (query) {
    debounceSearch()
  }
}

export const filterItemsWithNestedSearch = <DataItem>(
  allItems: DataItem[],
  query: string,
  identifierKeys: {
    displayKey: keyof DataItem
    idKey: keyof DataItem
    parentKey: keyof DataItem
  },
  toggleExpand?: (idsToExpand: string[]) => void,
) => {
  // Existing implementation...
  const { displayKey, idKey, parentKey } = identifierKeys

  // If query is empty or less than 3 characters, return all items instead of empty array
  if (!query || query.length < 3) return allItems

  // Cache for search results to avoid repeated expensive operations
  const queryLower = query.toLowerCase()
  const matches = new Set<string>()
  const matchedNodes = new Set<string>() // Move this up

  // Create indices for faster lookups
  const parentMap = new Map<string, string | null>()
  const childMap = new Map<string, string[]>()

  // Build all data structures in a single pass through the data
  for (let i = 0; i < allItems.length; i++) {
    const item = allItems[i]
    const itemId = String(item[idKey])
    const parentId = item[parentKey] != null ? String(item[parentKey]) : null

    // Check for matches during the same iteration
    const displayValue = String(item[displayKey]).toLowerCase()
    if (displayValue.includes(queryLower)) {
      matchedNodes.add(itemId)
    }

    // Build parent-child relationships
    if (parentId) {
      parentMap.set(itemId, parentId)
      if (!childMap.has(parentId)) {
        childMap.set(parentId, [])
      }
      childMap.get(parentId)!.push(itemId)
    } else {
      parentMap.set(itemId, null)
    }
  }

  // Early exit if no matches
  if (matchedNodes.size === 0) {
    return []
  }

  // Use a more efficient ancestor collection algorithm with early termination
  const collectAncestors = (nodeId: string) => {
    const ancestorChain: string[] = []
    let currentId: string | null = nodeId

    // First collect the chain of ancestors
    while (currentId !== null) {
      if (matches.has(currentId)) return // Early exit if we hit a node already in matches
      ancestorChain.push(currentId)
      currentId = parentMap.get(currentId) ?? null
    }

    // Then add all ancestors to matches in one go
    for (const id of ancestorChain) {
      matches.add(id)
    }
  }

  // More efficient descendants collection using iterative BFS with batch processing
  const collectDescendants = (nodeId: string) => {
    // If we already processed this node's descendants, skip
    if (matches.has(nodeId)) return

    const queue = [nodeId]
    matches.add(nodeId) // Add starting node

    // Process in small batches for better performance
    const BATCH_SIZE = 250
    let processedCount = 0

    while (queue.length > 0) {
      const currentId = queue.shift()!
      const children = childMap.get(currentId) || []

      // Add all children to matches and queue in a single batch
      for (const childId of children) {
        if (!matches.has(childId)) {
          matches.add(childId)
          queue.push(childId)
        }
      }

      // Throttle processing to prevent long-running loops
      processedCount++
      if (processedCount >= BATCH_SIZE && queue.length > 0) {
        // Allow UI thread to breathe for very large trees
        setTimeout(() => {
          // Process remaining nodes in the next tick
          while (queue.length > 0) {
            collectDescendants(queue.shift()!)
          }
        }, 0)
        break
      }
    }
  }

  // Process matched nodes more efficiently - use array iterator instead of forEach
  // Use Set iterator directly:
  for (const nodeId of matchedNodes) {
    collectAncestors(nodeId)
    collectDescendants(nodeId)
  }

  // Convert to array only once and cache the result
  const expandedIdsArray = Array.from(matches)

  // Use the toggleExpand function with the precomputed array
  toggleExpand?.(expandedIdsArray)

  // Use Set.has() for O1 lookups instead of includes() for filtering
  return allItems.filter((item) => matches.has(String(item[idKey])))
}

// Simplified debouncedSearch function
export const debouncedSearchRefactored = <DataItem>({
  query,
  identifierKeys,
  setFilteredItems,
  nestedConfigData,
  toggleExpand,
}: NestedMultiselectSearchProps<DataItem>) => {
  // Use a faster debounce time for better responsiveness
  const debounceSearch = debounce(() => {
    // Only show loading and perform search filtering for queries with 3+ characters
    if (query && query.length >= 3) {
      // Use setTimeout to prevent UI freezing for all datasets
      // This ensures the loading state is displayed before heavy computation starts
      setTimeout(() => {
        const results = filterItemsWithNestedSearch(
          nestedConfigData ?? [],
          query,
          identifierKeys,
          toggleExpand,
        )
        setFilteredItems(results)
      }, 0)
    } else {
      // For short/empty queries, return all items
      setFilteredItems(nestedConfigData)
    }
  }, 300) // Reduced from 500ms for better UX

  // Always run the search to handle both filtering and showing all items
  debounceSearch()
}

/**
 * Toggles the expanded state of one or multiple items.
 * @param {string | string[]} idOrIds - The ID(s) of the item(s) to toggle or expand.
 * @param {React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>} setExpandedItems -
 * A React state updater function to modify the expanded state of items.
 */
export const toggleExpand = (
  idOrIds: string | string[],
  setExpandedItems: React.Dispatch<
    React.SetStateAction<{
      [key: string]: boolean
    }>
  >,
) => {
  setExpandedItems((prevExpandedItems) => {
    const updatedExpandedItems = { ...prevExpandedItems }

    if (Array.isArray(idOrIds)) {
      // Handle an array of IDs when user searches for a specific record.
      idOrIds.forEach((id) => {
        // Ensure the item is expanded
        updatedExpandedItems[id] = true
      })
    } else {
      // Handle a single ID when user expand a record.
      updatedExpandedItems[idOrIds] = !prevExpandedItems[idOrIds]
    }

    return updatedExpandedItems
  })
}
